<script setup lang="ts">
import { captureException } from "@sentry/vue";
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { App } from "@capacitor/app";
import { useI18n } from "vue-i18n";

import PinCode from "./TcPinCode.vue";
import { TcButton as Button, TcModal } from "@/Components/UI";

import useAppStore from "@/Modules/App/Store";
import useAuthStore from "@/Modules/Auth/Store";

import { isApp } from "@/Utils/Ionic";
import { log } from "@/Utils/Debug";
import { isValidString } from "@/Utils/String";

import { config } from "@/Common/envConfig";

import logoSrc from "@/Assets/images/icon-192.png";
import usePrivacyStore from "@/Modules/Privacy/Store";

const appStore = useAppStore();
const authStore = useAuthStore();
const privacyStore = usePrivacyStore();
const { t } = useI18n();

const needProtection = ref<boolean>(false);
const needPin = ref<boolean>(false);
const newPin = ref<string>("");
const canLogout = ref<boolean>(false);
const isErrorPin = ref<boolean>(false);
const isCloseSessionModalOpened = ref<boolean>(false);

const logger = (...args: unknown[]) => {
  log("PRIVACY", ...args);
};

const isValidPin = computed(
  () => isValidString(newPin.value) && newPin.value.trim().length === config.privacyCodeLength
);

const handleChangePin = (val: string) => {
  newPin.value = val;
};

const handleConfirm = () => {
  isErrorPin.value = false;

  if (isValidPin.value && privacyStore.validatePin(newPin.value)) {
    logger("pin valido");
    needProtection.value = false;
  } else {
    logger("pin invalido");
    canLogout.value = true;
    isErrorPin.value = true;
  }
};

const showCloseSessionModal = () => {
  isCloseSessionModalOpened.value = true;
};

const hideCloseSessionModal = () => {
  isCloseSessionModalOpened.value = false;
};

const logout = () => {
  authStore.logout();

  needProtection.value = false;

  try {
    appStore.removeHubSpot();
  } catch (e) {
    captureException(e);
  }
};

onBeforeMount(() => {
  if (isApp()) {
    needPin.value = false;
    logger("IS PRIVATE", privacyStore.isPrivate);

    if (!authStore.isUserLogged) {
      logger("NOT AUTH");

      needProtection.value = false;
      privacyStore.setIsPrivate(false);

      return;
    }

    if (privacyStore.isPrivate) {
      logger("IS LOGUED", authStore.isUserLogged);

      if (authStore.isUserLogged) {
        logger("Need protection", privacyStore.needProtection());

        if (privacyStore.needProtection()) {
          needProtection.value = true;
          needPin.value = true;
        } else {
          needProtection.value = false;
        }
      } else {
        needProtection.value = false;
        privacyStore.setIsPrivate(false);
      }
    }
  }
});

onMounted(() => {
  if (isApp()) {
    App.addListener("appStateChange", async ({ isActive }) => {
      if (!isActive && privacyStore.isPrivate && !needProtection.value) {
        privacyStore.updatePinDate();
      }

      appStore.updateAppStatus(isActive);
      needPin.value = false;
      isErrorPin.value = false;

      if (!authStore.isUserLogged) {
        logger("NOT AUTH onMounted");

        needProtection.value = false;
        privacyStore.setIsPrivate(false);

        return;
      }

      logger("CHANGE APP STATE");
      if (isActive && privacyStore.needProtection()) {
        needProtection.value = true;

        if (privacyStore.isBioEnabled() && !privacyStore.isBioUsed()) {
          const isVerified = await privacyStore.checkBiometric(true);

          logger(`BIO CHECHER -------------- ${isVerified}`);

          if (isVerified) {
            needProtection.value = false;
          } else {
            needPin.value = true;

            logger("BIO NO VERIFICADO");
          }
        } else {
          needPin.value = true;
          canLogout.value = true;
        }
      } else if (isActive && !privacyStore.needProtection()) {
        needProtection.value = false;
      }

      logger("App state changed. Is active?", isActive);
    });

    App.addListener("appRestoredResult", (data) => {
      logger("Restored state:", data);
    });
  }
});
</script>

<template>
  <slot v-if="!needProtection || !isApp()" />

  <div class="privacy_view" v-else :class="{ 'privacy_view--pin': needPin }">
    <div class="privacy_view__pin" v-if="needPin">
      <p class="text-center typography-low-l color-content-mid mb-8">
        {{ t("privacy.description") }}
      </p>

      <PinCode
        name="privacy_screen_app"
        :class="{ 'mb-8': !isErrorPin, 'mb-2': isErrorPin }"
        :length="config.privacyCodeLength"
        :hasError="isErrorPin"
        @change="handleChangePin"
      />

      <p class="color-semantic-error mb-8 typography-low-s text-center" v-if="isErrorPin">
        {{ t("privacy.pin_error") }}
      </p>

      <Button class="privacy_view__pin__confirm" :disabled="!isValidPin" @click="handleConfirm">
        {{ t("privacy.confirm") }}
      </Button>

      <TcModal
        v-if="isCloseSessionModalOpened"
        :acceptButtonText="t('settings.modal.logout')"
        :rejectButtonText="t('settings.modal.cancel')"
        :title="t('settings.modal.close_session')"
        :textContent="t('settings.modal.are_you_sure_to_close_session')"
        @acceptButtonClicked="logout"
        @rejectButtonClicked="hideCloseSessionModal"
      />

      <Button
        v-if="canLogout"
        class="privacy_view__pin__logout mt-5"
        variant="no_border"
        @click="showCloseSessionModal"
      >
        <span class="privacy_view__logout__text">{{ t("settings.modal.logout") }}</span>
      </Button>
    </div>

    <img v-else class="privacy_view__logo" :src="logoSrc" alt="therapyside logo" />
  </div>
</template>

<style scoped lang="scss">
.privacy_view {
  @include sp.padding(b, 23);

  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  &--pin {
    @include colors.bg(background, base);

    align-items: start;
  }

  &__logo {
    height: 10vh;
  }

  &__pin {
    @include sp.margin(t, 20);
    @include sp.padding(x, 4);

    max-width: 400px;
    width: 100%;

    &__confirm {
      max-width: 240px;
      margin: auto;
      display: block;
    }

    &__logout {
      @include sp.margin(b, 4);

      flex-grow: 0;

      &__text {
        @include text.general(high, s);
        @include colors.color(content, high);
      }
    }
  }
}
</style>
